import { API_CONFIG, API_ENDPOINTS, authFetch, getHeadersWithOrg } from './client';
import {
  ComponentSchemaGroup,
  LayoutComponentDescriptor,
  SampleElement,
} from '../../features/template-designer/model/types';

/**
 * The placeable layout-component catalog (Title/Dates/Prolog/Search) lives on the
 * publishing/kg service — the owner of the built-in components — under the same
 * `/flameback/api/v1` prefix as `getComponents`. It is DELIBERATELY separate from
 * the DITA-content `/components` catalog (Phase 3.1), preserving the layout-vs-DITA
 * segregation. Read-only, not site-scoped (built-ins are global).
 *
 * The endpoint returns `[]` until Phase 3.2 lands the built-in bundles; the mapper
 * below mirrors the contract so those descriptors flow through unchanged when they
 * arrive.
 */
const LAYOUT_COMPONENTS_PATH = API_ENDPOINTS.LAYOUT_COMPONENTS;

/** Wire shape of a layout-component sample block: `children_html` is snake_case. */
interface RawLayoutSample {
  el: SampleElement;
  children_html: string;
}

/** Wire shape of a schema group: snake_case to match the rest of the response. */
interface RawSchemaGroup {
  group: string;
  variant_key?: string;
  variant_options?: string[];
  fields: ComponentSchemaGroup['fields'];
}

/** Wire shape of one layout-component descriptor: snake_case as the backend emits it. */
interface RawLayoutComponentDescriptor {
  id: string;
  label: string;
  source: LayoutComponentDescriptor['source'];
  tag: string;
  kind_class: string;
  schema?: RawSchemaGroup[];
  style: Record<string, unknown>;
  html?: string;
  css?: string;
  css_is_template?: boolean;
  js?: string;
  sample?: RawLayoutSample | null;
}

/** Map a wire schema group to the camelCase model shape (field keys stay verbatim). */
function mapGroup(group: RawSchemaGroup): ComponentSchemaGroup {
  return {
    group: group.group,
    variantKey: group.variant_key,
    variantOptions: group.variant_options,
    fields: group.fields,
  };
}

/** Map a wire descriptor to the camelCase model shape. */
function mapDescriptor(item: RawLayoutComponentDescriptor): LayoutComponentDescriptor {
  return {
    id: item.id,
    label: item.label,
    source: item.source,
    tag: item.tag,
    kindClass: item.kind_class,
    schema: item.schema?.map(mapGroup),
    style: item.style,
    html: item.html,
    css: item.css,
    cssIsTemplate: item.css_is_template,
    js: item.js,
    sample: item.sample
      ? { el: item.sample.el, childrenHtml: item.sample.children_html }
      : null,
  };
}

/**
 * Fetch the placeable layout-component catalog from the publishing/kg service.
 *
 * Static for a session (built-in components, not per-site data), so callers cache
 * the result. A non-OK status throws; the caller degrades to an empty catalog.
 */
export async function getLayoutComponents(): Promise<LayoutComponentDescriptor[]> {
  const response = await authFetch(`${API_CONFIG.BASE_URL}${LAYOUT_COMPONENTS_PATH}`, {
    headers: getHeadersWithOrg(),
  });
  if (!response.ok) {
    throw new Error(`Failed to load layout components: ${response.status}`);
  }
  const raw = (await response.json()) as RawLayoutComponentDescriptor[];
  return raw.map(mapDescriptor);
}
